import { getApolloClient } from '../lib/getApolloClient';
import {
  ASSIGN_USER,
  UNASSIGN_USER,
  GET_JOB,
  GET_ALL_JOBS_BY_STATUS_FOR_USER_ID,
  GET_ALL_JOBS_FOR_TENANT,
  GET_ALL_JOBS_FOR_TENANT_BY_DATE_RANGE,
  GET_ALL_JOBS_FOR_CLIENT,
  GET_ALL_JOBS_FOR_CLIENT_BY_DATE_RANGE,
  DELETE_JOB,
  CANCEL_JOB,
  CREATE_JOB,
  UPDATE_JOB,
  RETRIGGER_NOTIFICATIONS_FOR_ACTION,
} from './job.gql';
import type { AssignmentRateOverrides } from './assignmentRateOverrides.type';
import { GET_JOB_POSTING_QUESTIONNAIRE } from './questionnaire.gql';
import {
  PaginatedJobResponse,
  CreateJobInput,
  JobStatus,
  UpdateJobInput,
  QuestionnaireInput,
  Questionnaire,
  NotificationAction,
} from '../generated/gql-types';
import { handleGraphQLErrors } from '../util/graphqlError.util';

const createJob = async (
  job: CreateJobInput,
  questionnaire?: QuestionnaireInput,
): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: CREATE_JOB,
      variables: {
        input: job,
        ...(questionnaire && { questionnaire }),
      },
    });

    const { errors } = response;

    handleGraphQLErrors(errors);

    return response;
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

const getJobPostingQuestionnaire = async (
  tenantId?: string,
): Promise<Questionnaire | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_JOB_POSTING_QUESTIONNAIRE,
      variables: {
        ...(tenantId && { tenantId }),
      },
    });
    return data?.getJobPostingQuestionnaire || null;
  } catch (error) {
    console.error('Error fetching job posting questionnaire:', error);
    return null;
  }
};

const deleteJob = async (jobId: string): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: DELETE_JOB,
      variables: { id: jobId },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in deleteJob:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

const cancelJob = async (jobId: string, reason: string): Promise<boolean> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: CANCEL_JOB,
      variables: { id: jobId, reason },
    });

    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in cancelJob:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response.data?.cancelJob ?? false;
  } catch (error) {
    throw error;
  }
};

const retriggerNotificationsForAction = async (
  jobId: string,
  action: NotificationAction = NotificationAction.JobPosted,
): Promise<boolean> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: RETRIGGER_NOTIFICATIONS_FOR_ACTION,
      variables: {
        action,
        objectId: jobId,
      },
    });

    handleGraphQLErrors(response.errors);

    return response.data?.retriggerNotificationsForAction ?? false;
  } catch (error) {
    console.error('Error retriggering job notifications:', error);
    throw error;
  }
};

const getJob = async (jobId: string): Promise<any> => {
  try {
    const response = await getApolloClient().query({
      query: GET_JOB,
      variables: { id: jobId },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in getJob:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error fetching job:', error);
    throw error;
  }
};

const getAllJobsByStatusForUserId = async (
  status?: JobStatus[],
  userId?: string,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<PaginatedJobResponse | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_ALL_JOBS_BY_STATUS_FOR_USER_ID,
      variables: {
        status,
        ...(userId && { userId }),
        ...(limit && { limit }),
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      },
    });
    if (data?.getAllJobsByStatusForUserId) {
      return {
        jobs: data.getAllJobsByStatusForUserId.jobs,
        hasMore: data.getAllJobsByStatusForUserId.hasMore,
        lastEvaluatedKey: data.getAllJobsByStatusForUserId.lastEvaluatedKey,
      };
    }
    return {
      jobs: [],
      hasMore: false,
      lastEvaluatedKey: null,
    };
  } catch (error) {
    console.error('Error fetching jobs by status:', error);
    return {
      jobs: [],
      hasMore: false,
      lastEvaluatedKey: null,
    };
  }
};

const getAllJobsForTenant = async (
  tenantId: string,
  status?: JobStatus,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<any> => {
  try {
    const response = await getApolloClient().query({
      query: GET_ALL_JOBS_FOR_TENANT,
      variables: { tenantId, status, limit, lastEvaluatedKey },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in getAllJobsForTenant:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error fetching jobs for tenant:', error);
    throw error;
  }
};

const getAllJobsForTenantByDateRange = async (
  tenantId: string,
  dateFrom: string,
  dateTo: string,
  status?: JobStatus,
  limit?: number,
  lastEvaluatedKey?: string,
  includeApplicationCounts?: boolean,
): Promise<any> => {
  try {
    const response = await getApolloClient().query({
      query: GET_ALL_JOBS_FOR_TENANT_BY_DATE_RANGE,
      variables: {
        tenantId,
        dateTo,
        dateFrom,
        status,
        limit,
        lastEvaluatedKey,
        includeApplicationCounts,
      },
    });
    if (response.errors && response.errors.length > 0) {
      console.error(
        'GraphQL errors in getAllJobsForTenantByDateRange:',
        response.errors,
      );
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error fetching jobs for tenant by date range:', error);
    throw error;
  }
};

const getAllJobsForClientByDateRange = async (
  clientId: string,
  dateFrom: string,
  dateTo: string,
  status?: JobStatus,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<any> => {
  try {
    const response = await getApolloClient().query({
      query: GET_ALL_JOBS_FOR_CLIENT_BY_DATE_RANGE,
      variables: {
        clientId,
        dateFrom,
        dateTo,
        status,
        limit,
        lastEvaluatedKey,
      },
    });
    if (response.errors && response.errors.length > 0) {
      console.error(
        'GraphQL errors in getAllJobsForClientByDateRange:',
        response.errors,
      );
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error fetching jobs for client by date range:', error);
    throw error;
  }
};

const getAllJobsForClient = async (
  clientId: string,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<any> => {
  try {
    const response = await getApolloClient().query({
      query: GET_ALL_JOBS_FOR_CLIENT,
      variables: { clientId, limit, lastEvaluatedKey },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in getAllJobsForClient:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error fetching jobs for client:', error);
    throw error;
  }
};

const assignUser = async (
  id: string,
  userId: string,
  rateOverrides?: AssignmentRateOverrides,
): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: ASSIGN_USER,
      variables: { id, userId, ...rateOverrides },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in assignUser:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error assigning user:', error);
    throw error;
  }
};

const unassignUser = async (id: string, reason?: string): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: UNASSIGN_USER,
      variables: { id, reason },
    });

    // Check for GraphQL errors in the response
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in unassignUser:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response;
  } catch (error) {
    console.error('Error unassigning user:', error);
    throw error;
  }
};

const updateJob = async (
  jobId: string,
  input: UpdateJobInput,
): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: UPDATE_JOB,
      variables: { id: jobId, input },
    });

    handleGraphQLErrors(response.errors);

    return response;
  } catch (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const jobActions = {
  updateJob,
  assignUser,
  unassignUser,
  createJob,
  deleteJob,
  cancelJob,
  retriggerNotificationsForAction,
  getJob,
  getAllJobsByStatusForUserId,
  getAllJobsForTenant,
  getAllJobsForClient,
  getAllJobsForClientByDateRange,
  getAllJobsForTenantByDateRange,
  getJobPostingQuestionnaire,
};
